'use client';

import { useEffect, useState, useCallback } from 'react';
import { notificationService, type Notification } from '@/services/notificationService';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationData } from '@/components/common/RealtimeNotification';

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [realtimeNotification, setRealtimeNotification] = useState<NotificationData | null>(null);

  // Load initial notifications
  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const [notificationsResult, countResult] = await Promise.all([
        notificationService.getNotifications(50),
        notificationService.getUnreadCount()
      ]);

      if (notificationsResult.error) {
        throw notificationsResult.error;
      }

      if (countResult.error) {
        throw countResult.error;
      }

      setNotifications(notificationsResult.data || []);
      setUnreadCount(countResult.count);
      setError(null);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    const { success, error } = await notificationService.markAsRead(notificationId);
    
    if (success) {
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } else if (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    const { count, error } = await notificationService.markAllAsRead();
    
    if (!error && count > 0) {
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } else if (error) {
      console.error('Error marking all as read:', error);
    }
  }, []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = notificationService.subscribeToNotifications(
      user.id,
      (newNotification) => {
        // Add to notifications list
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Show real-time toast notification
        setRealtimeNotification({
          id: newNotification.id,
          title: newNotification.title,
          message: newNotification.message,
          type: getNotificationType(newNotification.type),
          timestamp: new Date(newNotification.created_at),
          autoDismiss: true
        });
      },
      (error) => {
        console.error('Real-time notification error:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  // Load notifications on mount
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Helper to map notification type to toast type
  const getNotificationType = (type: string): 'success' | 'error' | 'info' | 'warning' => {
    switch (type) {
      case 'order_approved': case'order_shipped':
        return 'success';
      case 'order_rejected':
        return 'error';
      case 'estimate_sent': case'order_status_change':
        return 'info';
      default:
        return 'info';
    }
  };

  const dismissRealtimeNotification = useCallback(() => {
    setRealtimeNotification(null);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    realtimeNotification,
    markAsRead,
    markAllAsRead,
    refresh: loadNotifications,
    dismissRealtimeNotification
  };
}